import { useDeviceQuery } from '@almbrand/usedevicequery';
import { getFileExtension } from 'common/DocumentService';
import { formatMessage } from 'common/MessagesServicesMappers';
import { FormattedCustomerDocument } from 'common/MessagesServicesTypes';
import { DownloadTracking } from 'common/trackingUtils';
import { Item, MultiColItemCard } from 'components/2-molecules/MultiColItemCard';
import { PreviewModal } from 'components/2-molecules/PreviewModal/PreviewModal';
import { HttpStatusCode } from 'constantsValues';
import useDocumentPreview from 'hooks/useDocumentPreview';
import { useThemeName } from 'hooks/useThemeName';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchDocuments } from 'store/slices/documentSlice';
import { getStatusMessage } from 'utilities/statusMessageUtils';
import './MessageComponent.scss';

export interface MessagesListComponentProps {
	contentType?: string;
	contentId?: number;
	title?: string;

	statusMessages?: {
		items: Array<{
			content: {
				statusMessage: string;
				statusCode: HttpStatusCode;
			};
		}>;
	};
}

const MessagesComponent: React.FC<MessagesListComponentProps> = ({ contentType, contentId, title, statusMessages }) => {
	const dispatch = useAppDispatch();
	const docsToUse = useAppSelector((state) => state.documents.data);
	const status = useAppSelector((state) => state.documents.status);
	const partyStatus = useAppSelector((state) => state.party.status);

	const navigate = useNavigate();
	const { isMobile, isTablet } = useDeviceQuery();
	const trackingThemeName = useThemeName();

	const { openModal, previewDocument, handleMessageClick, handleMessageDownload, onModalClose } =
		useDocumentPreview();

	useEffect(() => {
		if (status === HttpStatusCode.Idle) {
			dispatch(fetchDocuments());
		}
	}, [status, dispatch]);

	// Format documents to match the InsuranceList props
	const formattedDocs = (formatMessage(docsToUse) as FormattedCustomerDocument[]).map((doc) => ({
		id: doc.id, // Use the document ID as the unique identifier
		content_type: doc.content_type, // Use the content type for download
		icon: 'document', // Static icon identifier for messages
		title: doc.name, // Use the document title
		subtitle: doc.formattedDate, // Use the document date as a subtitle
		downloadItem: doc, // Document data for download
	}));

	const onMessageClick = (doc: Item) => {
		const screenSize = isMobile || isTablet ? 'download' : 'preview';
		const downloadTracking = DownloadTracking(
			doc.title,
			getFileExtension(doc.content_type),
			screenSize,
			trackingThemeName
		);
		handleMessageClick(doc.id, doc.title, doc.subtitle, downloadTracking);
	};

	if (partyStatus !== HttpStatusCode.OK) return null;

	return (
		<div className='message-component'>
			<MultiColItemCard
				title={title || 'Beskeder'} // Set title dynamically for InsuranceList
				items={formattedDocs} // Pass formatted documents
				onMoreClick={() => navigate('/beskeder')} // Navigate to all messages
				moreClickDestination='/beskeder'
				onItemClick={(doc) => onMessageClick(doc)}
				showError={status === HttpStatusCode.InternalServerError || status === HttpStatusCode.NoContent}
				errorMessage={getStatusMessage(statusMessages, status) ?? 'Der er ingen tilgængelige data.'}
			/>

			{openModal && (
				<PreviewModal
					displayAsPdf={previewDocument.displayAsPdf}
					data={previewDocument.data}
					handleMessageDownload={handleMessageDownload}
					onClose={onModalClose}
				/>
			)}
		</div>
	);
};

export default MessagesComponent;
